import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import User, { UserGroup } from "admin/types/interfaces/user";
import { AlertCircle, Check, ChevronRight, Loader2, Search, Users, X } from "lucide-react";
import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import { useDebounce } from "~/hooks/useDebounce";
import { cn } from "~/lib/utils";
import useGroups from "~/pages/admin/hooks/useGroups";
import useUsers from "~/pages/admin/hooks/useUsers";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { AccessDetailsView, buildExpandedAccess } from "./AccessDetailsView";
import {
  type AccessLevelOption,
  type ShareCollaborators,
  type ShareGroup,
  type ShareUser,
} from "./types";

export type { AccessLevelOption, ShareCollaborators, ShareGroup, ShareUser };

// Surface the backend's message (e.g. the "keep at least one owner" 409) rather
// than the JSON-stringified body an ApiError carries verbatim.
const shareErrorMessage = (e: unknown, fallback: string): string => {
  if (isApiError(e)) return getApiErrorMessage(e);
  return e instanceof Error ? e.message : fallback;
};

const MIN_QUERY_LENGTH = 3;

type InviteChip =
  | {
      id: string;
      type: "user";
      label: string;
      avatarUrl?: string;
      initials: string;
      metadata: User;
    }
  | {
      id: string;
      type: "group";
      label: string;
      initials: string;
      metadata: UserGroup;
    };

interface UserSuggestion {
  id: string;
  type: "user";
  label: string;
  sub?: string;
  avatarUrl?: string;
  metadata: User;
}

interface GroupSuggestion {
  id: string;
  type: "group";
  label: string;
  metadata: UserGroup;
}

type Suggestion = UserSuggestion | GroupSuggestion;

export interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceLabel: string;
  resourceName?: string;
  collaborators: ShareCollaborators | undefined;
  collaboratorsLoading?: boolean;
  collaboratorsError?: unknown;
  accessLevels: Record<string, AccessLevelOption>;
  defaultAccessLevel: string;
  onShare: (
    recipient: { user?: ShareUser; group?: ShareGroup },
    accessLevel: string | null
  ) => Promise<void>;
  currentUserEmail?: string | null;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-0.5 mt-3.5">
    <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
      {children}
    </span>
  </div>
);

const REMOVE_VALUE = "__remove__";

const SelectItemWithDesc = forwardRef<
  HTMLDivElement,
  { value: string; label: string; description?: string; className?: string }
>(({ value, label, description, className }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    value={value}
    className={cn(
      "flex w-full cursor-pointer select-none flex-col rounded-sm px-2 py-1.5 outline-none focus:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
  >
    <div className="flex items-center gap-1.5">
      <span className="flex size-3.5 shrink-0 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-3.5" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>
        <span className="text-sm font-medium leading-none">{label}</span>
      </SelectPrimitive.ItemText>
    </div>
    {description && (
      <span className="mt-0.5 pl-5 text-xs text-muted-foreground">{description}</span>
    )}
  </SelectPrimitive.Item>
));
SelectItemWithDesc.displayName = "SelectItemWithDesc";

interface MemberRowProps {
  id: string;
  label: string;
  sub?: string;
  accessLevel: string | null;
  isGroup: boolean;
  isCurrentUser?: boolean;
  isOwnerLocked?: boolean;
  imageUrl?: string;
  accessLevels: Record<string, AccessLevelOption>;
  onChangeRole: (id: string, role: string) => void;
  onRemove: (id: string) => void;
}

const MemberRow = ({
  id,
  label,
  sub,
  accessLevel,
  isGroup,
  isCurrentUser,
  isOwnerLocked,
  imageUrl,
  accessLevels,
  onChangeRole,
  onRemove,
}: MemberRowProps) => {
  const disabled = isCurrentUser || isOwnerLocked;

  return (
    <div className="flex items-center gap-3 py-2">
      {isGroup ? (
        <span className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-muted">
          <Users className="size-[19px] text-muted-foreground" />
        </span>
      ) : (
        <Avatar className="size-[34px] shrink-0">
          <AvatarImage src={imageUrl || undefined} />
          <AvatarFallback className="text-xs">{label.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{label}</span>
        </div>
        {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
      </div>

      <Select
        value={accessLevel ?? ""}
        onValueChange={(val) => {
          if (val === REMOVE_VALUE) {
            onRemove(id);
          } else {
            onChangeRole(id, val);
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger size="sm" className="w-26 shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-[1300] min-w-[220px]">
          {Object.entries(accessLevels).map(([key, opt]) => (
            <SelectItemWithDesc
              key={key}
              value={key}
              label={opt.label}
              description={opt.description}
            />
          ))}
          <SelectSeparator />
          <SelectItemWithDesc
            value={REMOVE_VALUE}
            label="Remove"
            className="text-destructive focus:text-destructive"
          />
        </SelectContent>
      </Select>
    </div>
  );
};

interface SearchChipsFieldProps {
  chips: InviteChip[];
  onAddChip: (chip: InviteChip) => void;
  onRemoveChip: (id: string) => void;
  inviteRole: string;
  onInviteRoleChange: (role: string) => void;
  accessLevels: Record<string, AccessLevelOption>;
  excludedEmails: string[];
  excludedGroupNames: string[];
  currentUserEmail?: string | null;
}

const SearchChipsField = ({
  chips,
  onAddChip,
  onRemoveChip,
  inviteRole,
  onInviteRoleChange,
  accessLevels,
  excludedEmails,
  excludedGroupNames,
  currentUserEmail,
}: SearchChipsFieldProps) => {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [roleSelectOpen, setRoleSelectOpen] = useState(false);
  const debouncedQuery = useDebounce(query.trim());
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const enabled = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const { data: users = [], isLoading: usersLoading } = useUsers({
    searchQuery: debouncedQuery,
    enabled,
    activeOnly: true,
  });
  const { data: groups = [], isLoading: groupsLoading } = useGroups({
    searchQuery: debouncedQuery,
    enabled,
  });

  const chipIds = new Set(chips.map((c) => c.id));
  const alreadyAddedIds = new Set([
    ...excludedEmails.map((e) => e.toLowerCase()),
    ...excludedGroupNames,
  ]);

  const suggestions: Suggestion[] = [
    ...users
      .filter((u) => !chipIds.has(u.email))
      .map(
        (u): UserSuggestion => ({
          id: u.email,
          type: "user",
          label: u.full_name || u.email,
          sub: u.full_name ? u.email : undefined,
          avatarUrl: u.image_url,
          metadata: u,
        })
      ),
    ...groups
      .filter((g) => !chipIds.has(g.name))
      .map(
        (g): GroupSuggestion => ({
          id: g.name,
          type: "group",
          label: g.display_name || g.name,
          metadata: g,
        })
      ),
  ];

  const isLoading = usersLoading || groupsLoading;
  // Gate both popovers on the live length so they never overlap; fetch stays debounced.
  const trimmedLength = query.trim().length;
  const showSuggestions =
    focused && !roleSelectOpen && enabled && trimmedLength >= MIN_QUERY_LENGTH;
  const showHint =
    focused && !roleSelectOpen && trimmedLength > 0 && trimmedLength < MIN_QUERY_LENGTH;

  const handleSelect = (s: Suggestion) => {
    const chip: InviteChip =
      s.type === "user"
        ? {
            id: s.id,
            type: "user",
            label: s.label,
            avatarUrl: s.avatarUrl,
            initials: getInitials(s.label),
            metadata: s.metadata,
          }
        : {
            id: s.id,
            type: "group",
            label: s.label,
            initials: getInitials(s.label),
            metadata: s.metadata,
          };
    onAddChip(chip);
    setQuery("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div
        onClick={() => {
          setFocused(true);
          inputRef.current?.focus();
        }}
        className="flex min-h-9 cursor-text items-stretch rounded-md border border-border bg-background"
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5 p-1.5 pl-2.5">
          <Search className="size-[15px] shrink-0 text-muted-foreground" />

          {chips.map((chip) => (
            <span
              key={chip.id}
              className="inline-flex h-6 max-w-[200px] items-center gap-1.5 rounded-full border border-border bg-muted pl-1 pr-0.5 text-xs font-medium text-foreground"
            >
              {chip.type === "group" ? (
                <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-accent">
                  <Users className="size-3 text-muted-foreground" />
                </span>
              ) : chip.avatarUrl ? (
                <img
                  src={chip.avatarUrl}
                  alt=""
                  className="size-[18px] shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-accent text-[9px] font-semibold text-accent-foreground">
                  {chip.initials}
                </span>
              )}
              <span className="truncate">{chip.label}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveChip(chip.id);
                }}
                className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="size-[10px]" />
              </button>
            </span>
          ))}

          <input
            ref={inputRef}
            className="min-w-[80px] flex-1 border-none bg-transparent py-0.5 text-xs text-foreground outline-none placeholder:text-muted-foreground"
            placeholder={chips.length === 0 ? "Search by name, email, or group" : ""}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
          />
        </div>

        <div className="flex shrink-0 items-center">
          <div className="my-1 w-px self-stretch bg-border" />
          <Select
            value={inviteRole}
            onValueChange={onInviteRoleChange}
            onOpenChange={(open) => {
              setRoleSelectOpen(open);
              if (open) {
                setFocused(false);
              }
            }}
          >
            <SelectTrigger
              className="w-26 border-none px-3 py-0.5 text-xs font-medium shadow-none focus-visible:ring-0"
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[1300] min-w-[220px]">
              {Object.entries(accessLevels).map(([key, opt]) => (
                <SelectItemWithDesc
                  key={key}
                  value={key}
                  label={opt.label}
                  description={opt.description}
                />
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showSuggestions && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 max-h-[260px] overflow-auto rounded-lg border border-border bg-popover p-1 shadow-lg">
          {isLoading ? (
            <div className="flex items-center gap-2 px-2.5 py-3 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Searching…
            </div>
          ) : suggestions.length === 0 ? (
            <div className="px-2.5 py-3 text-xs text-muted-foreground">No results found</div>
          ) : (
            suggestions.map((s) => {
              const alreadyAdded = alreadyAddedIds.has(
                s.type === "user" ? s.id.toLowerCase() : s.id
              );
              return (
                <button
                  key={`${s.type}:${s.id}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={alreadyAdded ? undefined : () => handleSelect(s)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left",
                    alreadyAdded
                      ? "cursor-default opacity-50"
                      : "cursor-pointer hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  {s.type === "group" ? (
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
                      <Users className="size-[15px] text-muted-foreground" />
                    </span>
                  ) : (
                    <Avatar className="size-7 shrink-0">
                      <AvatarImage src={s.type === "user" ? s.avatarUrl : undefined} />
                      <AvatarFallback className="text-[10px]">
                        {s.label.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-foreground">{s.label}</p>
                    {s.type === "user" && s.sub && (
                      <p className="truncate text-xs text-muted-foreground">{s.sub}</p>
                    )}
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {alreadyAdded ? "Already added" : s.type === "group" ? "Group" : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}

      {showHint && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 rounded-lg border border-border bg-popover p-1 shadow-lg">
          <div className="px-2.5 py-3 text-xs text-muted-foreground">
            Type at least {MIN_QUERY_LENGTH} characters to search
          </div>
        </div>
      )}
    </div>
  );
};

const buildRecipient = (
  chip: InviteChip,
  accessLevel: string
): { user?: ShareUser; group?: ShareGroup } => {
  if (chip.type === "user") {
    return {
      user: {
        email: chip.metadata.email,
        full_name: chip.metadata.full_name,
        image_url: chip.metadata.image_url,
        access_level: accessLevel,
      },
    };
  }
  return {
    group: {
      name: chip.metadata.name,
      display_name: chip.metadata.display_name,
      is_internal: chip.metadata.is_internal,
      access_level: accessLevel,
      users: [],
    },
  };
};

export const ShareModal = ({
  open,
  onOpenChange,
  resourceLabel,
  resourceName,
  collaborators,
  collaboratorsLoading,
  collaboratorsError,
  accessLevels,
  defaultAccessLevel,
  onShare,
  currentUserEmail,
}: ShareModalProps) => {
  const { data: allUsers = [] } = useUsers({ enabled: open });
  const userImageMap = useMemo(
    () => new Map(allUsers.map((u) => [u.email.toLowerCase(), u.image_url])),
    [allUsers]
  );
  const { showSuccess, showError } = useToast();

  const [chips, setChips] = useState<InviteChip[]>([]);
  const [inviteRole, setInviteRole] = useState(defaultAccessLevel);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmRemoval, setConfirmRemoval] = useState<{
    id: string;
    label: string;
    isGroup: boolean;
    user?: ShareUser;
    group?: ShareGroup;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [view, setView] = useState<"share" | "details">("share");

  const users = collaborators?.users ?? [];
  const groups = collaborators?.groups ?? [];

  const ownerCount = [...users, ...groups].filter((c) => c.access_level === "owner").length;

  const handleClose = () => {
    setView("share");
    setChips([]);
    setInviteRole(defaultAccessLevel);
    onOpenChange(false);
  };

  const handleInvite = async () => {
    if (!chips.length) {
      return;
    }
    setIsSaving(true);
    try {
      await Promise.all(chips.map((chip) => onShare(buildRecipient(chip, inviteRole), inviteRole)));
      setChips([]);
      showSuccess("Access updated");
    } catch (e: unknown) {
      showError(shareErrorMessage(e, "Failed to share"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRoleChange = async (
    recipient: { user?: ShareUser; group?: ShareGroup },
    newRole: string
  ) => {
    try {
      await onShare(recipient, newRole);
      showSuccess("Access updated");
    } catch (e: unknown) {
      showError(shareErrorMessage(e, "Failed to update access"));
    }
  };

  const handleRemoveRequest = (
    id: string,
    label: string,
    isGroup: boolean,
    user?: ShareUser,
    group?: ShareGroup
  ) => {
    setConfirmRemoval({ id, label, isGroup, user, group });
  };

  const handleConfirmRemoval = async () => {
    if (!confirmRemoval) {
      return;
    }
    setIsRemoving(true);
    try {
      await onShare({ user: confirmRemoval.user, group: confirmRemoval.group }, null);
      showSuccess(`${confirmRemoval.label} removed`);
      setConfirmRemoval(null);
    } catch (e: unknown) {
      showError(shareErrorMessage(e, "Failed to remove access"));
    } finally {
      setIsRemoving(false);
    }
  };

  const sortedUsers = [...users].sort((a, b) =>
    (a.full_name || a.email).localeCompare(b.full_name || b.email)
  );

  const hasGroups = groups.length > 0;
  const totalUserCount = useMemo(
    () => buildExpandedAccess(users, groups, accessLevels).length,
    [users, groups, accessLevels]
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) {
            handleClose();
          } else {
            onOpenChange(true);
          }
        }}
      >
        <DialogContent className="gap-2 p-0 sm:max-w-[560px]" showCloseButton={false}>
          {view === "details" ? (
            <>
              <DialogTitle className="sr-only">All users with access</DialogTitle>
              <AccessDetailsView
                resourceName={resourceName ?? resourceLabel}
                users={users}
                groups={groups}
                accessLevels={accessLevels}
                currentUserEmail={currentUserEmail}
                userImageMap={userImageMap}
                onBack={() => setView("share")}
                onClose={() => handleClose()}
              />
            </>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 px-[22px] pb-3 pt-5">
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-semibold leading-tight tracking-tight">
                    Share {resourceLabel}
                  </DialogTitle>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {resourceName && `${resourceName} · `}
                    {totalUserCount} {totalUserCount === 1 ? "user" : "users"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleClose()}
                  className="mt-[-2px] flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                >
                  <X className="size-[18px]" />
                </button>
              </div>

              <div className="relative px-[22px]">
                <SearchChipsField
                  chips={chips}
                  onAddChip={(chip) => setChips((prev) => [...prev, chip])}
                  onRemoveChip={(id) => setChips((prev) => prev.filter((c) => c.id !== id))}
                  inviteRole={inviteRole}
                  onInviteRoleChange={setInviteRole}
                  accessLevels={accessLevels}
                  excludedEmails={users.map((u) => u.email)}
                  excludedGroupNames={groups.map((g) => g.name)}
                  currentUserEmail={currentUserEmail}
                />
              </div>

              <div className="max-h-[360px] overflow-y-auto px-[22px] pb-1.5 pt-0.5">
                {collaboratorsLoading ? (
                  <div className="flex flex-col gap-3 py-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="size-[34px] rounded-full" />
                        <Skeleton className="h-4 flex-1 rounded" />
                        <Skeleton className="h-[30px] w-26 rounded-md" />
                      </div>
                    ))}
                  </div>
                ) : collaboratorsError ? (
                  <Alert variant="destructive" className="mx-1 my-2">
                    <AlertCircle className="size-4" />
                    <AlertDescription>
                      Failed to load access list. Please try again.
                    </AlertDescription>
                  </Alert>
                ) : sortedUsers.length === 0 && groups.length === 0 ? (
                  <div className="mx-1 my-4 rounded-lg border border-dashed border-border bg-muted px-4 py-7 text-center">
                    <p className="text-sm font-medium text-foreground">Only you have access</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Add users or groups above to start sharing this {resourceLabel.toLowerCase()}.
                    </p>
                  </div>
                ) : (
                  <>
                    {sortedUsers.length > 0 && (
                      <>
                        <SectionLabel>Users · {sortedUsers.length}</SectionLabel>
                        <div>
                          {sortedUsers.map((user) => {
                            const isSelf =
                              user.email.toLowerCase() === currentUserEmail?.toLowerCase();
                            const isOnlyOwner = ownerCount === 1 && user.access_level === "owner";
                            return (
                              <MemberRow
                                key={user.email}
                                id={user.email}
                                label={user.full_name || user.email}
                                sub={user.full_name ? user.email : undefined}
                                accessLevel={user.access_level}
                                isGroup={false}
                                isCurrentUser={isSelf}
                                isOwnerLocked={isOnlyOwner}
                                imageUrl={userImageMap.get(user.email.toLowerCase())}
                                accessLevels={accessLevels}
                                onChangeRole={(_, role) => handleRoleChange({ user }, role)}
                                onRemove={() =>
                                  handleRemoveRequest(
                                    user.email,
                                    user.full_name || user.email,
                                    false,
                                    user,
                                    undefined
                                  )
                                }
                              />
                            );
                          })}
                        </div>
                      </>
                    )}

                    {groups.length > 0 && (
                      <>
                        <SectionLabel>Groups · {groups.length}</SectionLabel>
                        <div>
                          {groups.map((group) => {
                            const isOnlyOwner = ownerCount === 1 && group.access_level === "owner";
                            return (
                              <MemberRow
                                key={group.name}
                                id={group.name}
                                label={group.display_name ?? group.name}
                                accessLevel={group.access_level}
                                isGroup={true}
                                isOwnerLocked={isOnlyOwner}
                                accessLevels={accessLevels}
                                onChangeRole={(_, role) => handleRoleChange({ group }, role)}
                                onRemove={() =>
                                  handleRemoveRequest(
                                    group.name,
                                    group.display_name ?? group.name,
                                    true,
                                    undefined,
                                    group
                                  )
                                }
                              />
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-border px-[22px] pb-4 pt-3">
                <button
                  type="button"
                  onClick={() => setView("details")}
                  className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  View all users with access
                  <ChevronRight className="size-3.5" />
                </button>

                <Button
                  onClick={chips.length > 0 ? handleInvite : handleClose}
                  loading={isSaving}
                  disabled={isSaving}
                  size="sm"
                  className="h-[34px] px-4 text-sm"
                >
                  {chips.length > 0 ? "Share" : "Done"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmRemoval} onOpenChange={(v) => !v && setConfirmRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <strong>{confirmRemoval?.label}</strong> from this{" "}
              {resourceLabel.toLowerCase()}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleConfirmRemoval} loading={isRemoving}>
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
